import {
  Button,
  Drawer,
  Group,
  Stack,
  Text,
  Textarea,
  Select,
  TextInput,
  Badge,
} from '@mantine/core';
import { useState } from 'react';
import type { StageRow } from './StageTab';

export interface EpisodeActionDrawerProps {
  open: boolean;
  onClose: () => void;
  action: string | null;
  row: StageRow | null;
  onConfirm?: (action: string, payload: unknown) => void;
}

const PROVIDER_OPTIONS = [
  { value: 'dr-smith', label: 'Dr. John Smith' },
  { value: 'dr-doe', label: 'Dr. Jane Doe' },
  { value: 'dr-miller', label: 'Dr. Sarah Miller' },
  { value: 'dr-park', label: 'Dr. John Park' },
];

export function EpisodeActionDrawer({ open, onClose, action, row, onConfirm }: EpisodeActionDrawerProps): JSX.Element {
  const [notes, setNotes] = useState('');
  const [provider, setProvider] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [date, setDate] = useState('');

  // Reset state when opening a new action
  const handleClose = () => {
    setNotes('');
    setProvider(null);
    setOutcome(null);
    setReason(null);
    setDate('');
    onClose();
  };

  const handleSubmit = () => {
    onConfirm?.(action!, { notes, provider, outcome, reason, date });
    handleClose();
  };

  if (!row || !action) return <></>;

  // Common components
  const NotesField = () => (
    <Textarea label="Notes" placeholder="Add relevant notes..." value={notes} onChange={(e) => setNotes(e.currentTarget.value)} minRows={3} />
  );
  const SubmitButtons = ({ label = 'Confirm' }: { label?: string }) => (
    <Group justify="flex-end" mt="md">
      <Button variant="subtle" onClick={handleClose}>Cancel</Button>
      <Button onClick={handleSubmit}>{label}</Button>
    </Group>
  );

  const renderContent = () => {
    switch (action) {
      // New Client
      case 'Send Reminder':
        return (
          <Stack gap="md">
            <Text size="sm">Send an email reminder to <strong>{row.clientName as string}</strong> to complete their intake form.</Text>
            <SubmitButtons label="Send Reminder" />
          </Stack>
        );
      case 'Discard':
        return (
          <Stack gap="md">
            <Text size="sm" c="red">Are you sure you want to discard this partial submission for <strong>{row.clientName as string}</strong>?</Text>
            <SubmitButtons label="Discard" />
          </Stack>
        );

      // Waitlist
      case 'Assign Provider':
        return (
          <Stack gap="md">
            <Select label="Select Provider" data={PROVIDER_OPTIONS} value={provider} onChange={setProvider} placeholder="Choose provider..." />
            <NotesField />
            <SubmitButtons label="Assign" />
          </Stack>
        );
      case 'Move to Matching':
      case 'Move to Active':
        return (
          <Stack gap="md">
            <Text size="sm">Confirm moving this episode to <strong>{action.replace('Move to ', '')}</strong>.</Text>
            <NotesField />
            <SubmitButtons />
          </Stack>
        );
      case 'Add Note':
      case 'Add Notes':
        return (
          <Stack gap="md">
            <NotesField />
            <SubmitButtons label="Save Note" />
          </Stack>
        );

      // Screening
      case 'View Screening Answers':
        return (
          <Stack gap="md">
            <Text size="sm" fw={600}>Screening Results</Text>
            {['Emergency/crisis', 'Hospitalization', 'Suicidal ideation', 'Medications', 'Substance use', 'Legal proceedings'].map((q, i) => {
              const isYes = (row.flaggedQuestions as string[])?.includes(q) || (i === 0 && (row.screeningYesCount as number) > 0);
              return (
                <Group key={q} justify="space-between">
                  <Text size="sm">{q}</Text>
                  <Badge color={isYes ? 'red' : 'gray'} variant="light">{isYes ? 'Yes' : 'No'}</Badge>
                </Group>
              );
            })}
            <Button mt="md" variant="light" onClick={handleClose}>Close</Button>
          </Stack>
        );
      case 'Record Outcome':
        return (
          <Stack gap="md">
            <Select label="Meeting Outcome" data={[
              { value: 'good-fit', label: 'Good Fit (Proceed to Matching/Active)' },
              { value: 'not-a-fit', label: 'Not a Fit (Move to Inactive)' },
              { value: 'no-show', label: 'No Show' }
            ]} value={outcome} onChange={setOutcome} placeholder="Select outcome..." />
            <NotesField />
            <SubmitButtons label="Save Outcome" />
          </Stack>
        );
      case 'Reschedule':
      case 'Book First Session':
        return (
          <Stack gap="md">
            <TextInput label="Date & Time" type="datetime-local" value={date} onChange={(e) => setDate(e.currentTarget.value)} />
            <NotesField />
            <SubmitButtons label={action === 'Reschedule' ? 'Reschedule Appointment' : 'Book Session'} />
          </Stack>
        );
      case 'Override Risk Level':
        return (
          <Stack gap="md">
            <Text size="sm">
              This will downgrade the client&apos;s risk level to <strong>Standard</strong> and bypass further screening.
            </Text>
            <NotesField />
            <SubmitButtons label="Override to Standard Risk" />
          </Stack>
        );
      case 'Move to Inactive':
      case 'Deactivate':
        return (
          <Stack gap="md">
            <Select label="Reason" data={[
              { value: 'not-a-fit', label: 'Not a clinical fit' },
              { value: 'client-withdrew', label: 'Client withdrew/no-show' },
              { value: 'completed', label: 'Completed treatment' }
            ]} value={reason} onChange={setReason} placeholder="Select reason..." />
            <NotesField />
            <SubmitButtons label="Deactivate Episode" />
          </Stack>
        );

      // Matching
      case 'Score Providers':
        return (
          <Stack gap="md">
            <Text size="sm">
              Run the automated provider scoring algorithm for this episode&apos;s preferences and insurance.
            </Text>
            <SubmitButtons label="Run Scoring" />
          </Stack>
        );
      case 'Assign to Consultation Team':
      case 'Match to Regular Provider':
        return (
          <Stack gap="md">
            <Select label="Select Provider" data={PROVIDER_OPTIONS} value={provider} onChange={setProvider} placeholder="Choose provider..." />
            <NotesField />
            <SubmitButtons label="Assign Provider" />
          </Stack>
        );
      case 'View Preferences':
        return (
          <Stack gap="md">
            <Text size="sm" fw={600}>Client Preferences</Text>
            <Text size="sm">{row.preferencesSummary as string ?? 'No specific preferences recorded.'}</Text>
            <Button mt="md" variant="light" onClick={handleClose}>Close</Button>
          </Stack>
        );
      case 'View Screening Notes':
        return (
          <Stack gap="md">
            <Text size="sm" fw={600}>Screening Notes</Text>
            <Text size="sm">{row.screeningOutcomeNotes as string ?? 'No screening notes recorded.'}</Text>
            <Button mt="md" variant="light" onClick={handleClose}>Close</Button>
          </Stack>
        );

      // Active
      case 'Initiate Transfer':
        return (
          <Stack gap="md">
            <Select label="Transfer to Provider (Optional)" data={PROVIDER_OPTIONS} value={provider} onChange={setProvider} placeholder="Any available provider" />
            <Textarea label="Transfer Reason" placeholder="Why is this transfer requested?" value={reason ?? ''} onChange={(e) => setReason(e.currentTarget.value)} minRows={3} />
            <SubmitButtons label="Submit Transfer Request" />
          </Stack>
        );
      case 'Request Rematch':
        return (
          <Stack gap="md">
            <Text size="sm">This will move the episode back to the Matching stage.</Text>
            <Textarea label="Rematch Reason" placeholder="Why does the client need a new match?" value={reason ?? ''} onChange={(e) => setReason(e.currentTarget.value)} minRows={3} />
            <SubmitButtons label="Request Rematch" />
          </Stack>
        );

      // Transfer
      case 'Approve Transfer':
        return (
          <Stack gap="md">
            <Text size="sm">Approve the transfer to <strong>{row.toProvider as string ?? 'the requested provider'}</strong>?</Text>
            <NotesField />
            <SubmitButtons label="Approve Transfer" />
          </Stack>
        );
      case 'Reject Transfer':
        return (
          <Stack gap="md">
            <Textarea label="Rejection Reason (required)" value={reason ?? ''} onChange={(e) => setReason(e.currentTarget.value)} minRows={3} />
            <SubmitButtons label="Reject Transfer" />
          </Stack>
        );
      case 'Force Complete':
        return (
          <Stack gap="md">
            <Text size="sm">Force complete this transfer immediately?</Text>
            <NotesField />
            <SubmitButtons label="Force Complete" />
          </Stack>
        );
      case 'View Details':
        return (
          <Stack gap="md">
            <Text size="sm" fw={600}>Transfer Details</Text>
            <Text size="sm"><strong>From:</strong> {row.fromProvider as string}</Text>
            <Text size="sm"><strong>To:</strong> {row.toProvider as string}</Text>
            <Text size="sm"><strong>Reason:</strong> {row.transferReason as string}</Text>
            <Button mt="md" variant="light" onClick={handleClose}>Close</Button>
          </Stack>
        );

      // Inactive
      case 'Reactivate':
        return (
          <Stack gap="md">
            <Select label="Reactivate to Stage" data={[
              { value: 'matching', label: 'Matching' },
              { value: 'active', label: 'Active' }
            ]} value={outcome} onChange={setOutcome} placeholder="Select target stage..." />
            <NotesField />
            <SubmitButtons label="Reactivate Episode" />
          </Stack>
        );

      default:
        return (
          <Stack gap="md">
            <Text size="sm">You are performing the <strong>{action}</strong> action on this record.</Text>
            <NotesField />
            <SubmitButtons />
          </Stack>
        );
    }
  };

  return (
    <Drawer opened={open} onClose={handleClose} position="right" size="md" title={action}>
      {renderContent()}
    </Drawer>
  );
}
